import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import BasicInfoFields from './BasicInfoFields';
import ThemeField from './ThemeField';
import SpinsLimitField from './SpinsLimitField';
import IsSharedField from './IsSharedField';
import InstantDrawField from './InstantDrawField';
import DrawDateField from './DrawDateField';
import CustomFieldsSection from './CustomFieldsSection';
import PrizeInstructionsField from './PrizeInstructionsField';
import TermsEditor from './TermsEditor';
import PrerequisiteEditor from './PrerequisiteEditor';
import LoserMessageField from './LoserMessageField';
import ScheduledMessageField from './ScheduledMessageField';
import { WheelFormData } from '../../hooks/useWheelForm';

interface WheelFormProps {
  formData: WheelFormData;
  formErrors: Partial<Record<keyof WheelFormData, string>>;
  isEditing: boolean;
  isSubmitting: boolean;
  isFreePlan: boolean;
  themes: any[];
  onInputChange: (field: keyof WheelFormData, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const WheelForm: React.FC<WheelFormProps> = ({
  formData,
  formErrors,
  isEditing,
  isSubmitting,
  isFreePlan,
  themes,
  onInputChange,
  onSubmit,
  onCancel
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCustomizations, setShowCustomizations] = useState(false);

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {/* Informações Essenciais */}
      <div className="space-y-3">
        <BasicInfoFields
          name={formData.name}
          status={formData.status}
          errors={{
            name: formErrors.name,
            slug: formErrors.slug
          }}
          onChange={(field, value) => onInputChange(field, value)}
        />

        <InstantDrawField
          instantDraw={formData.instantDraw !== undefined ? formData.instantDraw : true}
          onChange={(value) => onInputChange('instantDraw', value)}
        />

        {/* Modo Simplificado */}
        <div className="border border-tinta/15 rounded-lg p-4 bg-creme-light/50">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.simplifiedForm || false}
              onChange={(e) => onInputChange('simplifiedForm', e.target.checked)}
              className="mt-0.5 rounded border-tinta/30 text-bordo focus:ring-bordo/40 focus:ring-offset-0 cursor-pointer"
            />
            <div>
              <span className="text-sm font-bold text-tinta">
                Modo Simplificado
              </span>
              <p className="text-xs text-tinta-soft italic mt-0.5">
                Não pedir WhatsApp e data de nascimento. Apenas o nome do participante.
              </p>
            </div>
          </label>
          {formData.simplifiedForm && (
            <div className="mt-3 bg-mostarda/15 border border-mostarda/40 rounded-lg p-3 text-sm text-tinta">
              <p className="text-xs">
                <strong className="font-bold">Modo simplificado ativo:</strong> Ideal para eventos rápidos. O formulário pedirá apenas o nome.
                Limite de giros funcionará apenas por IP (sem WhatsApp para controlar).
                Notificações WhatsApp não serão enviadas automaticamente.
              </p>
            </div>
          )}
        </div>

        <DrawDateField
          drawDate={formData.drawDate || null}
          instantDraw={formData.instantDraw !== undefined ? formData.instantDraw : true}
          onChange={(value) => onInputChange('drawDate', value)}
        />
      </div>

      {/* Configurações Avançadas - Colapsável */}
      <div className="border border-tinta/15 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between px-4 py-3 bg-creme-light/50 hover:bg-creme-light transition-colors"
        >
          <span className="text-xs font-bold uppercase tracking-wider text-tinta">
            Configurações Avançadas {!showAdvanced && <span className="text-tinta-soft italic normal-case tracking-normal font-normal">(opcional)</span>}
          </span>
          {showAdvanced ? (
            <ChevronUp className="h-4 w-4 text-tinta-soft" />
          ) : (
            <ChevronDown className="h-4 w-4 text-tinta-soft" />
          )}
        </button>

        {showAdvanced && (
          <div className="p-4 space-y-3 bg-white border-t border-tinta/10">
            {!isFreePlan && (
              <ThemeField
                themeId={formData.themeId || null}
                themes={themes}
                onChange={(value) => onInputChange('themeId', value)}
              />
            )}

            <SpinsLimitField
              spinsPerWhatsapp={formData.spinsPerWhatsapp || 0}
              blockByIp={formData.blockByIp || false}
              onChange={(value) => onInputChange('spinsPerWhatsapp', value)}
              onBlockByIpChange={(value) => onInputChange('blockByIp', value)}
            />
            {formData.simplifiedForm && formData.spinsPerWhatsapp && formData.spinsPerWhatsapp > 0 ? (
              <p className="text-xs text-mostarda-dark italic -mt-1">
                No modo simplificado, o limite de giros funciona apenas por IP (sem WhatsApp).
              </p>
            ) : null}

            <IsSharedField
              isShared={formData.isShared !== undefined ? formData.isShared : true}
              onChange={(value) => onInputChange('isShared', value)}
            />

            <PrizeInstructionsField
              value={formData.prizePickupInstructions || ''}
              onChange={(value) => onInputChange('prizePickupInstructions', value)}
            />
          </div>
        )}
      </div>

      {/* Personalizações - Colapsável */}
      <div className="border border-tinta/15 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowCustomizations(!showCustomizations)}
          className="w-full flex items-center justify-between px-4 py-3 bg-creme-light/50 hover:bg-creme-light transition-colors"
        >
          <span className="text-xs font-bold uppercase tracking-wider text-tinta">
            Personalizações {!showCustomizations && <span className="text-tinta-soft italic normal-case tracking-normal font-normal">(opcional)</span>}
          </span>
          {showCustomizations ? (
            <ChevronUp className="h-4 w-4 text-tinta-soft" />
          ) : (
            <ChevronDown className="h-4 w-4 text-tinta-soft" />
          )}
        </button>

        {showCustomizations && (
          <div className="p-4 space-y-3 bg-white border-t border-tinta/10">
            <TermsEditor
              termsType={formData.termsType}
              termsContent={formData.termsContent}
              onTermsTypeChange={(type) => onInputChange('termsType', type)}
              onTermsContentChange={(content) => onInputChange('termsContent', content)}
            />

            <PrerequisiteEditor
              prerequisiteContent={formData.prerequisiteContent}
              onPrerequisiteContentChange={(content) => onInputChange('prerequisiteContent', content)}
            />

            <LoserMessageField
              value={formData.loserMessage || ''}
              onChange={(value) => onInputChange('loserMessage', value)}
            />

            <ScheduledMessageField
              value={formData.scheduledMessage || ''}
              onChange={(value) => onInputChange('scheduledMessage', value)}
            />

            <CustomFieldsSection
              customFields={formData.customFields || []}
              onChange={(fields) => onInputChange('customFields', fields)}
            />
          </div>
        )}
      </div>

      {/* Botões fixos e destacados */}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t border-tinta/10 sticky bottom-0 bg-white -mx-4 sm:-mx-6 px-4 sm:px-6 pb-4 z-10">
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/30 transition-colors"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto px-5 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark focus:outline-none focus:ring-2 focus:ring-bordo/40 disabled:opacity-50 transition-colors"
        >
          {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar Sorteio' : 'Criar Sorteio'}
        </button>
      </div>
    </form>
  );
};

export default WheelForm;
